import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Loader2, Check, Copy } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { usePageTranslation } from "@/i18n/useTranslation";
import type { TranslationRefDoctype } from "@/lib/erp/contentTranslations";
import {
  useItemTranslations,
  useSaveItemTranslations,
} from "../hooks/useItemTranslations";

export interface TranslatableFieldDef {
  fieldname: string;
  label: string;
  baseValue: string;
  multiline?: boolean;
}

const translations = {
  en: {
    title: "Translations",
    description: "Add translations for each language your store supports.",
    baseLabel: "Original",
    save: "Save translations",
    saving: "Saving…",
    saved: "Translations saved",
    copyToOthers: "Use for all languages",
    noLanguages:
      "No extra languages enabled yet. Enable languages in Storefront Settings → Languages first.",
    empty: "Leave a field empty to show the original text.",
  },
  th: {
    title: "คำแปล",
    description: "เพิ่มคำแปลสำหรับแต่ละภาษาที่ร้านของคุณรองรับ",
    baseLabel: "ต้นฉบับ",
    save: "บันทึกคำแปล",
    saving: "กำลังบันทึก…",
    saved: "บันทึกคำแปลแล้ว",
    copyToOthers: "ใช้กับทุกภาษา",
    noLanguages:
      "ยังไม่ได้เปิดใช้ภาษาเพิ่มเติม เปิดใช้ได้ที่ ตั้งค่าหน้าร้าน → ภาษา",
    empty: "เว้นว่างไว้เพื่อแสดงข้อความต้นฉบับ",
  },
};

/**
 * TranslateModal — per-record translation editor (Plan 1928).
 *
 * Shows one section per non-default enabled language with inputs for every
 * translatable field. Empty input = fall back to the original text.
 * Works for Items (name/descriptions) and Item Attributes (value labels).
 */
export function TranslateModal({
  open,
  onOpenChange,
  refDoctype,
  refName,
  fields,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  refDoctype: TranslationRefDoctype;
  refName: string;
  fields: TranslatableFieldDef[];
}) {
  const t = usePageTranslation(translations);
  const { data, isLoading } = useItemTranslations(refDoctype, refName, open);
  const saveMutation = useSaveItemTranslations(refDoctype, refName);

  // local edit state: { lang: { fieldname: value } }
  const [values, setValues] = useState<Record<string, Record<string, string>>>({});
  const prevOpen = useRef(false);

  const targetLanguages = useMemo(() => {
    if (!data) return [];
    return data.enabled_languages.filter((code) => code !== data.default_language);
  }, [data]);

  // Seed local state whenever the modal (re)opens and data arrives
  useEffect(() => {
    if (open && data && !prevOpen.current) {
      const seeded: Record<string, Record<string, string>> = {};
      for (const lang of data.enabled_languages) {
        seeded[lang] = {};
        for (const field of fields) {
          seeded[lang][field.fieldname] = data.languages?.[lang]?.[field.fieldname] ?? "";
        }
      }
      setValues(seeded);
      prevOpen.current = true;
    }
    if (!open) prevOpen.current = false;
  }, [open, data, fields]);

  const setFieldValue = useCallback((lang: string, fieldname: string, value: string) => {
    setValues((prev) => ({
      ...prev,
      [lang]: { ...prev[lang], [fieldname]: value },
    }));
  }, []);

  const copyToAllLanguages = useCallback(
    (fromLang: string, fieldname: string) => {
      setValues((prev) => {
        const source = prev[fromLang]?.[fieldname] ?? "";
        const next = { ...prev };
        for (const lang of targetLanguages) {
          if (lang === fromLang) continue;
          next[lang] = { ...next[lang], [fieldname]: source };
        }
        return next;
      });
    },
    [targetLanguages],
  );

  const handleSave = useCallback(async () => {
    const payload: Record<string, Record<string, string | null>> = {};
    for (const lang of targetLanguages) {
      payload[lang] = {};
      for (const field of fields) {
        const value = (values[lang]?.[field.fieldname] ?? "").trim();
        payload[lang][field.fieldname] = value || null; // null deletes → fallback
      }
    }
    await saveMutation.mutateAsync(payload);
    toast.success(t.saved);
    onOpenChange(false);
  }, [targetLanguages, fields, values, saveMutation, t, onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="md:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t.title}</DialogTitle>
          <DialogDescription>
            {t.description} {t.empty}
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : targetLanguages.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6">{t.noLanguages}</p>
        ) : (
          <div className="space-y-6 max-h-[60vh] overflow-y-auto pr-1">
            {/* Original values for reference */}
            <div className="rounded-xl bg-muted/40 p-4 space-y-2">
              <Badge variant="secondary">{`${t.baseLabel} (${data?.default_language || ""})`}</Badge>
              {fields.map((field) => (
                <div key={field.fieldname} className="text-sm">
                  <span className="text-muted-foreground">{field.label}: </span>
                  <span className="break-words">
                    {field.baseValue ? field.baseValue.replace(/<[^>]+>/g, " ").slice(0, 160) : "—"}
                  </span>
                </div>
              ))}
            </div>

            {targetLanguages.map((lang) => (
              <div key={lang} className="space-y-3">
                <Badge>{lang.toUpperCase()}</Badge>
                {fields.map((field) => (
                  <div key={field.fieldname} className="space-y-1">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs text-muted-foreground">{field.label}</Label>
                      {targetLanguages.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2 text-[11px] text-muted-foreground"
                          onClick={() => copyToAllLanguages(lang, field.fieldname)}
                        >
                          <Copy className="h-3 w-3 mr-1" />
                          {t.copyToOthers}
                        </Button>
                      )}
                    </div>
                    {field.multiline ? (
                      <Textarea
                        value={values[lang]?.[field.fieldname] ?? ""}
                        onChange={(e) => setFieldValue(lang, field.fieldname, e.target.value)}
                        placeholder={field.baseValue.replace(/<[^>]+>/g, " ").slice(0, 80)}
                        rows={4}
                      />
                    ) : (
                      <Input
                        value={values[lang]?.[field.fieldname] ?? ""}
                        onChange={(e) => setFieldValue(lang, field.fieldname, e.target.value)}
                        placeholder={field.baseValue}
                      />
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}

        {targetLanguages.length > 0 && (
          <div className="flex justify-end pt-2">
            <Button onClick={handleSave} disabled={saveMutation.isPending || isLoading}>
              {saveMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t.saving}
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  {t.save}
                </>
              )}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
